import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase-auth'
import { useAuth } from '../../../contexts/AuthContextLazy'
import { showInfo } from '../../../utils/toast'
import { 
  LinkIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

interface Integration {
  id: string
  name: string
  description: string
  icon: React.ComponentType<any>
  status: 'connected' | 'disconnected' | 'error'
  lastSync?: Date
  stats?: {
    label: string
    value: string | number
  }[]
  features: string[]
  href: string
}

export const Integrations: React.FC = () => {
  const navigate = useNavigate()
  const { hasRole } = useAuth()
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadIntegrations()
  }, [])

  const loadIntegrations = async () => {
    try {
      // Check MyMeetings connection status
      const { data: credentials } = await supabase
        .from('integration_credentials')
        .select('*')
        .eq('integration_type', 'mymeetings')
        .single()

      const myMeetingsIntegration: Integration = {
        id: 'mymeetings',
        name: 'MyMeetings',
        description: 'Sync client profiles, meeting summaries, and action items from your MyMeetings account',
        logo: '/assets/integrations/mymeetings-logo.png',
        icon: BuildingOffice2Icon,
        status: credentials ? 'connected' : 'disconnected',
        lastSync: credentials?.last_sync_at ? new Date(credentials.last_sync_at) : undefined,
        stats: credentials ? [
          { label: 'Synced Clients', value: credentials.sync_stats?.clients_synced || 0 },
          { label: 'Recent Meetings', value: credentials.sync_stats?.meetings_synced || 0 },
          { label: 'Action Items', value: credentials.sync_stats?.action_items || 0 }
        ] : [],
        features: [
          'Automatic client profile updates',
          'Meeting summary visibility', 
          'Action item tracking',
          'Smart client matching'
        ],
        href: '/portal/admin/integrations/mymeetings'
      }

      // Future integrations placeholders
      const futureIntegrations: Integration[] = [
        {
          id: 'google-calendar',
          name: 'Google Calendar',
          description: 'Sync meeting schedules and availability with Google Calendar',
          logo: '/assets/integrations/google-calendar-logo.svg',
          icon: CalendarDaysIcon,
          status: 'disconnected',
          features: [
            'Two-way calendar sync',
            'Automatic meeting creation',
            'Availability management',
            'Meeting reminders'
          ],
          href: '#'
        },
        {
          id: 'docusign',
          name: 'DocuSign',
          description: 'Streamline document signing and tracking for client agreements',
          logo: '/assets/integrations/docusign-logo.png',
          icon: DocumentTextIcon,
          status: 'disconnected',
          features: [
            'Document status tracking',
            'Automatic filing',
            'Signature reminders',
            'Compliance reports'
          ],
          href: '#'
        }
      ]

      setIntegrations([myMeetingsIntegration, ...futureIntegrations])
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error loading integrations:', error);
        }

      }
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async (integrationId: string) => {
    if (integrationId === 'mymeetings') {
      navigate('/portal/admin/integrations/mymeetings/connect')
    } else {
      // Future integrations
      showInfo('This integration is coming soon!')
    }
  }

  const getStatusColor = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return 'text-green-600 bg-green-100'
      case 'error':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status: Integration['status']) => {
    switch (status) {
      case 'connected':
        return 'Connected'
      case 'error':
        return 'Error'
      default:
        return 'Not Connected'
    }
  }

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Integration Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="relative bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
          >
            {/* Card Header */}
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {integration.logo ? (
                      <img 
                        src={integration.logo} 
                        alt={`${integration.name} logo`}
                        className="h-10 w-auto"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (integration.icon) {
                            const iconEl = e.currentTarget.nextElementSibling as HTMLElement;
                            iconEl?.classList.remove('hidden');
                          }
                        }}
                      />
                    ) : (
                      integration.icon && <integration.icon className="h-10 w-10 text-gray-600" />
                    )}
                    {integration.logo && integration.icon && (
                      <integration.icon className="h-10 w-10 text-gray-600 hidden" />
                    )}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {integration.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(integration.status)}`}>
                      {getStatusText(integration.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="mt-4 text-sm text-gray-600">
                {integration.description}
              </p>

              {/* Features */}
              <ul className="mt-4 space-y-2">
                {integration.features.slice(0, 3).map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="ml-2 text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Stats (if connected) */}
              {integration.status === 'connected' && integration.stats && (
                <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                  {integration.stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <p className="text-sm font-medium text-gray-900">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Last Sync (if connected) */}
              {integration.lastSync && (
                <p className="mt-4 text-xs text-gray-500">
                  Last synced: {new Intl.DateTimeFormat('en-US', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  }).format(integration.lastSync)}
                </p>
              )}
            </div>

            {/* Card Footer */}
            <div className="px-6 pb-6">
              {integration.status === 'connected' ? (
                <button
                  onClick={() => navigate(integration.href)}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  Manage
                  <ChevronRightIcon className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(integration.id)}
                  disabled={integration.href === '#'}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LinkIcon className="mr-2 h-4 w-4" />
                  {integration.href === '#' ? 'Coming Soon' : 'Connect'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationCircleIcon className="h-5 w-5 text-amber-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">
              Need help with integrations?
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                Our integration features are designed to save you time and streamline your workflow.
                If you need assistance setting up an integration or have questions about how they work,
                please contact our support team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}